import React from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { odeleLightTheme } from '../../theme';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log detailed error information
    console.group('🚨 React Error Boundary Caught an Error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Stack:', error.stack);
    console.error('Error Message:', error.message);
    console.error('Error Name:', error.name);
    console.error('Current URL:', window.location.href);
    console.error('User Agent:', navigator.userAgent);
    console.error('Timestamp:', new Date().toISOString());
    console.groupEnd();

    // Store error details in state for display
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <ChakraProvider theme={odeleLightTheme}>
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            fontFamily: 'Arial, sans-serif',
            maxWidth: '800px',
            margin: '50px auto'
          }}>
            <h1 style={{ color: '#e53e3e', marginBottom: '20px' }}>
              🚨 Something went wrong
            </h1>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              We've encountered an unexpected error. The development team has been notified.
            </p>
            
            {process.env.NODE_ENV === 'development' && (
              <details style={{ 
                textAlign: 'left', 
                background: '#f7fafc', 
                padding: '15px', 
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                  🔍 Error Details (Development Only)
                </summary>
                <div style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                  <p><strong>Error:</strong> {this.state.error?.message}</p>
                  <p><strong>Error Type:</strong> {this.state.error?.name}</p>
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre style={{ 
                        background: '#fff', 
                        padding: '10px', 
                        overflow: 'auto',
                        fontSize: '12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        marginTop: '5px'
                      }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                  {this.state.error?.stack && (
                    <div>
                      <strong>Error Stack:</strong>
                      <pre style={{ 
                        background: '#fff', 
                        padding: '10px', 
                        overflow: 'auto',
                        fontSize: '12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        marginTop: '5px'
                      }}>
                        {this.state.error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
            
            <button 
              onClick={() => window.location.reload()} 
              style={{
                background: '#38a169',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                marginRight: '10px'
              }}
            >
              🔄 Reload Page
            </button>
            
            <button 
              onClick={() => window.location.href = '/'} 
              style={{
                background: '#3182ce',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              🏠 Go Home
            </button>
          </div>
        </ChakraProvider>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 